// Kinhall roster messages went to everyone in the hall up to four times a second, each with the whole roster
// (up to 61 trainers, about 4 KB): at 300 players the biggest stream the server sent (423 KB/s, 2026-10-25), and it
// grows with the square of the crowd. Now they carry what changed since the last one: trainers new to the roster or
// whose look changed (whole), others who moved (name and spot), and names gone. The whole roster still goes to a
// trainer as they enter, and to everyone every HALL_FULL_MS, so a client can never drift for long.
import type { CompanionRef } from './protocol.ts';

/** The whole roster goes out at least this often while anything changes. */
export const HALL_FULL_MS = 10_000;

/** A trainer in the hall as other visitors see them. */
export interface HallTrainer {
  /** Name (unique, and what chat lines carry, so a speech bubble finds its speaker). */
  n: string;
  /** Worn code: outfit and look (shared/looks.ts). */
  o: string;
  lv: number;
  tag?: string;
  comp?: CompanionRef;
  x: number; y: number;
}

/** What changed in the hall since the last roster. */
export interface HallPatch {
  /** Trainers new to the roster, or whose look, level, guild or companion changed: whole. */
  d?: HallTrainer[];
  /** Others who moved: name, x, y, name, x, y... */
  p?: (string | number)[];
  /** Names gone from the roster. */
  g?: string[];
}

function sameComp(a?: CompanionRef, b?: CompanionRef): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.species === b.species && a.aura === b.aura && a.skin === b.skin;
}

/** The same trainer as shown, wherever they stand. */
function sameLook(a: HallTrainer, b: HallTrainer): boolean {
  return a.o === b.o && a.lv === b.lv && a.tag === b.tag && sameComp(a.comp, b.comp);
}

/** What changed from the last roster sent (by name) to this one; null when nothing did. */
export function hallPatch(prev: ReadonlyMap<string, HallTrainer>, next: readonly HallTrainer[]): HallPatch | null {
  const d: HallTrainer[] = [], p: (string | number)[] = [], g: string[] = [];
  const names = new Set<string>();
  for (const r of next) {
    names.add(r.n);
    const was = prev.get(r.n);
    if (!was || !sameLook(was, r)) d.push(r);
    else if (was.x !== r.x || was.y !== r.y) p.push(r.n, r.x, r.y);
  }
  for (const n of prev.keys()) if (!names.has(n)) g.push(n);
  if (!d.length && !p.length && !g.length) return null;
  return { ...(d.length ? { d } : {}), ...(p.length ? { p } : {}), ...(g.length ? { g } : {}) };
}

/**
 * Bring a client's copy of the roster up to date: a whole roster (list) replaces it, a patch changes it. A move
 * for a trainer it never heard of is skipped (the next whole roster brings them).
 */
export function applyHall(roster: Map<string, HallTrainer>, m: { list?: readonly HallTrainer[] } & HallPatch): void {
  if (m.list) {
    roster.clear();
    for (const r of m.list) roster.set(r.n, r);
    return;
  }
  for (const n of m.g ?? []) roster.delete(n);
  for (const r of m.d ?? []) roster.set(r.n, r);
  const p = m.p ?? [];
  for (let i = 0; i + 2 < p.length; i += 3) {
    const r = roster.get(String(p[i]));
    if (r) roster.set(r.n, { ...r, x: Number(p[i + 1]), y: Number(p[i + 2]) });
  }
}
